import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UpdateProfileBodyDto, UserDto } from '@picflow/types';
import { lastValueFrom } from 'rxjs';
import { AuthService } from '../auth/services/auth.service';
import { AppConfigService } from '../providers/config.service';
import { AnalyticsService } from './analytics.service';
import { CardService } from './card.service';
import { DataLoaderService } from './data-loader.service';
import { MergeAccountService } from './merge-account.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StateService } from './state.service';
import { StoryblokService } from './storyblok.service';
import { SubscriptionService } from './subscription.service';
import { TenantService } from './tenant.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  lastFetchTime: number;
  constructor(
    private state: StateService,
    private auth: AuthService,
    private http: HttpClient,
    private loader: DataLoaderService,
    private observableLoader: ObservableDataLoaderService,
    private config: AppConfigService,
    private analyticsService: AnalyticsService,
    private tenantService: TenantService,
    private subscriptionService: SubscriptionService,
    private cardService: CardService,
    private storyblokService: StoryblokService,
    private mergeAccountService: MergeAccountService,
    private route: ActivatedRoute
  ) {}

  async getUser(): Promise<UserDto> {
    if (this.lastFetchTime) {
      const timeSinceLastFetch = Date.now() - this.lastFetchTime;
      if (timeSinceLastFetch > 1000 * 60 * 5) {
        console.debug('Clearing user cache');
        this.clearUserCache();
      }
    }

    try {
      const user = await this.loader.get('user', () => this.getUserInternal());
      if (user && !this.state.plan) this.loader.remove('user');
      return user;
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.status !== 401 && err.status !== 403) throw err;
      return undefined;
    }
  }

  private checkSignedUp(user: UserDto) {
    if (!user) return;
    if (user.questionnaire.signedup) return;
    user.questionnaire.signedup = true;
    if (user.created_at > 1669368370) {
      // Before this date, signup was reported without updating the questionnaire so we don't want to report it twice
      this.analyticsService.track('signup');
    }
    lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/profile`, { questionnaire: user.questionnaire }));
  }

  private checkClickParams(user: UserDto) {
    if (!user) return;
    const newTrackParams = this.analyticsService.getTrackingParams();
    Object.keys(newTrackParams).forEach(key => {
      if (user['tracking_parameters'][key] === newTrackParams[key]) {
        delete newTrackParams[key];
      }
    });
    if (!Object.values(newTrackParams).some(Boolean)) return;
    user['tracking_parameters'] = newTrackParams;
    lastValueFrom(
      this.http.patch(`${this.config.apiEndpoint}/profile`, { tracking_parameters: user['tracking_parameters'] })
    );
  }

  private async getUserInternal() {
    this.lastFetchTime = Date.now();
    const tokenData = await this.auth.getTokenData();
    if (!tokenData) return;
    await this.tenantService.loadTenants();

    if (!this.state.activeTenant) {
      const profile = await this.tenantService.getProfileData();
      this.state.user = <UserDto>profile.user;
      if (!this.state.user.first_name) this.state.user.first_name = '';
      if (!this.state.user.last_name) this.state.user.last_name = '';
      this.analyticsService.identify();
      this.checkSignedUp(this.state.user);
      this.checkClickParams(this.state.user);
    } else {
      // loading card here even though we don't use it. It is still needed :)
      const [plan, , profile] = await Promise.all([
        this.subscriptionService.getPlan(),
        this.cardService.loadCard(),
        this.tenantService.getProfileData(),
      ]);
      this.state.user = <UserDto>profile.user;
      if (!this.state.user.first_name) this.state.user.first_name = '';
      if (!this.state.user.last_name) this.state.user.last_name = '';
      this.state.plan = plan;
      this.analyticsService.identify();
      this.checkSignedUp(this.state.user);
      this.checkClickParams(this.state.user);
    }

    this.mergeAccountService.autoMerge();

    return this.state.user;
  }

  async updateUser(data: UpdateProfileBodyDto): Promise<void> {
    await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/profile`, data));
    this.loader.remove('user');
    this.loader.remove('profile');
    this.observableLoader.remove('members');
    await this.getUser();
  }

  async changePassword(old_password: string, new_password: string) {
    try {
      await lastValueFrom(
        this.http.post(`${this.config.apiEndpoint}/auth/change-password`, {
          old_password,
          new_password,
        })
      );
      return true;
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.status !== 401 && err.status !== 403) throw err;
      return false;
    }
  }

  public async getCannyToken() {
    const resp = await lastValueFrom(
      this.http.post<{ token: string }>(`${this.config.apiEndpoint}/auth/canny/token`, {})
    );
    return resp.token;
  }

  public async isEnterpriseUser() {
    const resp = await this.getEnterpriseCompanyInfo();
    return !!resp;
  }

  public async getEnterpriseCompanyInfo(): Promise<{ name: string; image: string }> {
    const user = this.state.user;

    if (!user) return;
    const domain = user.email.split('@')[1];

    const resp = await lastValueFrom(
      this.storyblokService.getStories({ filter_query: [{ key: 'domain', value: domain }] }, this.route.snapshot)
    );

    const enterprise = resp[0];
    if (!enterprise) return;
    return {
      name: enterprise.name,
      image: enterprise.content.logo?.filename,
    };
  }

  public clearUserCache() {
    this.loader.remove('linkedAccounts');
    this.loader.remove('user');
    this.loader.remove('profile');
    this.loader.removeFuzzy('billingCustomer');
    this.loader.remove('plan-name_');
    this.loader.remove('scheduledUpdates');
    this.loader.remove('mySubscriptions');
  }
}
